if (!customElements.get("stl-carousel-slider")) {
  class StlCarouselSlider extends HTMLElement {
    constructor() {
      super();
      this.abortController = null;
      this.pendingPlayIndex = null;
      this.onSectionLoad = this.init.bind(this);

      if (Shopify.designMode) {
        window.addEventListener("shopify:section:load", this.onSectionLoad);
      }
    }

    connectedCallback() {
      if (typeof Swiper === "undefined") {
        if (document.readyState === "complete") {
          this.scheduleInit();
        } else {
          window.addEventListener("load", () => this.scheduleInit(), {
            once: true
          });
        }
      } else {
        this.scheduleInit();
      }
    }

    scheduleInit() {
      if (typeof Swiper === "undefined") return;
      this.init();
    }

    init() {
      if (typeof Swiper === "undefined") return;

      this.abortController?.abort();
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }

      this.pendingPlayIndex = null;

      const swiperEl = this.querySelector(".swiper") || this;
      const sliderRoot =
        this.closest(".stl-carousel__slider") || this.parentElement;
      const swiperOptions =
        JSON.parse(this.getAttribute("data-swiper-options")) || {};

      const isMobile = window.matchMedia("(max-width: 749px)").matches;
      const isDesktopWide = window.matchMedia("(min-width: 990px)").matches;
      const slideCount = swiperOptions.slideCount || 0;
      const canLoop = slideCount >= 2;
      const desktopSlidesPerView = Math.min(
        swiperOptions.slidesPerViewDesktop || 4,
        slideCount || 4
      );
      const centeredLoopPad = Math.ceil(desktopSlidesPerView / 2);
      const loopBuffer = centeredLoopPad;
      const useLoop = canLoop && !isDesktopWide;
      const initialIndex = isMobile
        ? (swiperOptions.initialSlideMobile ?? 1)
        : (swiperOptions.initialSlideDesktop ?? 0);
      const startIndex = Math.min(
        initialIndex,
        Math.max(slideCount - 1, 0)
      );

      const wrapper = swiperEl.querySelector(".swiper-wrapper");

      const sliderOptions = {
        centeredSlides: true,
        centeredSlidesBounds: false,
        loop: useLoop,
        loopAdditionalSlides: loopBuffer,
        loopPreventsSliding: false,
        slidesPerView: "auto",
        slidesPerGroup: 1,
        speed: 400,
        watchOverflow: false,
        observer: true,
        observeParents: true,
        allowTouchMove: true,
        spaceBetween: swiperOptions.spaceBetweenMobile || 0,
        breakpoints: {
          750: {
            slidesPerView: "auto",
            loopAdditionalSlides: loopBuffer,
            allowTouchMove: true,
            spaceBetween:
              swiperOptions.spaceBetweenTablet ||
              swiperOptions.spaceBetweenDesktop ||
              0
          },
          990: {
            slidesPerView: desktopSlidesPerView,
            slidesPerGroup: 1,
            centeredSlides: true,
            centeredSlidesBounds: true,
            loopAdditionalSlides: loopBuffer,
            loopedSlides: centeredLoopPad,
            allowTouchMove: true,
            spaceBetween: swiperOptions.spaceBetweenDesktop || 0
          }
        },
        on: {
          init: swiper => {
            this.bindInteractions(signal);
            this.bindNavigation(sliderRoot, swiperOptions, signal);
            requestAnimationFrame(() => {
              swiper.update();
              if (useLoop) {
                if (typeof swiper.loopFix === "function") {
                  swiper.loopFix();
                }
                swiper.slideToLoop(startIndex, 0, false);
              } else {
                swiper.slideTo(startIndex, 0, false);
              }
              this.playActiveSlide(swiper);
            });
          },
          resize: swiper => {
            swiper.update();
            if (useLoop && typeof swiper.loopFix === "function") {
              swiper.loopFix();
            }
          },
          slideChangeTransitionEnd: swiper => {
            if (this.pendingPlayIndex !== null) {
              const slide = this.getSlideByRealIndex(
                swiper,
                this.pendingPlayIndex
              );
              this.pendingPlayIndex = null;
              if (slide) {
                this.pauseAllExcept(slide);
                this.playSlideMedia(slide);
                return;
              }
            }

            this.playActiveSlide(swiper);
          }
        }
      };

      if (!useLoop) {
        sliderOptions.centeredSlidesBounds = true;
        sliderOptions.initialSlide = startIndex;
      }

      if (swiperOptions.autoplay) {
        sliderOptions.autoplay = {
          ...swiperOptions.autoplay
        };
      }

      this.swiper = new Swiper(swiperEl, sliderOptions);

      if (!this.breakpointListener) {
        const desktopQuery = window.matchMedia("(min-width: 990px)");
        this.breakpointListener = () => this.init();
        desktopQuery.addEventListener("change", this.breakpointListener);
      }
    }

    bindNavigation(sliderRoot, swiperOptions, signal) {
      if (!swiperOptions.navigation || !sliderRoot) return;

      const prevEl = sliderRoot.querySelector(swiperOptions.navigation.prevEl);
      const nextEl = sliderRoot.querySelector(swiperOptions.navigation.nextEl);
      if (!prevEl || !nextEl) return;

      const slideCount = swiperOptions.slideCount || 0;
      if (slideCount < 2) return;

      const goTo = realIndex => {
        const swiper = this.swiper;
        if (!swiper) return;

        if (swiper.params.loop) {
          swiper.slideToLoop(realIndex, swiper.params.speed);
        } else {
          swiper.slideTo(realIndex, swiper.params.speed);
        }
      };

      prevEl.addEventListener(
        "click",
        event => {
          event.preventDefault();
          const swiper = this.swiper;
          if (!swiper) return;
          goTo((swiper.realIndex - 1 + slideCount) % slideCount);
        },
        { signal }
      );

      nextEl.addEventListener(
        "click",
        event => {
          event.preventDefault();
          const swiper = this.swiper;
          if (!swiper) return;
          goTo((swiper.realIndex + 1) % slideCount);
        },
        { signal }
      );
    }

    bindInteractions(signal) {
      const handleMediaClick = event => {
        if (!this.swiper) return;
        if (event.target.closest(".stl-product-bar")) return;

        const media = event.target.closest(".stl-carousel__media");
        if (!media || !this.contains(media)) return;

        const slide = media.closest(".swiper-slide");
        if (!slide) return;

        const swiper = this.swiper;
        const slideIndex = this.getSlideIndex(slide);
        if (slideIndex === null || slideIndex < 0) return;

        this.pendingPlayIndex = slideIndex;
        this.pauseAllExcept(slide);
        this.playSlideMedia(slide);

        if (swiper.params.loop) {
          swiper.slideToLoop(slideIndex);
        } else {
          swiper.slideTo(slideIndex);
        }
      };

      this.addEventListener("click", handleMediaClick, {
        capture: true,
        signal
      });
    }

    getSlideIndex(slide) {
      const loopIndex = slide.getAttribute("data-swiper-slide-index");

      if (loopIndex !== null && loopIndex !== "") {
        return parseInt(loopIndex, 10);
      }

      return Array.from(slide.parentElement?.children || []).indexOf(slide);
    }

    getSlideByRealIndex(swiper, realIndex) {
      return (
        Array.from(swiper.slides).find(slide => {
          if (!slide.classList.contains("swiper-slide-active")) return false;
          const index = slide.getAttribute("data-swiper-slide-index");
          return index !== null && parseInt(index, 10) === realIndex;
        }) ||
        Array.from(swiper.slides).find(slide => {
          const index = slide.getAttribute("data-swiper-slide-index");
          return index !== null && parseInt(index, 10) === realIndex;
        })
      );
    }

    pauseSlideMedia(slide) {
      if (!slide) return;

      slide.querySelectorAll("video").forEach(video => video.pause());

      slide.querySelectorAll(".js-youtube").forEach(iframe => {
        iframe.contentWindow?.postMessage(
          '{"event":"command","func":"pauseVideo","args":""}',
          "*"
        );
      });

      slide.querySelectorAll(".js-vimeo").forEach(iframe => {
        iframe.contentWindow?.postMessage('{"method":"pause"}', "*");
      });
    }

    playSlideMedia(slide) {
      if (!slide) return;

      const mediaInner =
        slide.querySelector(
          ".stl-carousel__media-inner:not(.small-up-hide)"
        ) || slide.querySelector(".stl-carousel__media-inner");

      if (!mediaInner) return;

      const video = mediaInner.querySelector("video");
      if (video) {
        video.muted = true;
        const playPromise = video.play();
        if (playPromise !== undefined) {
          playPromise.catch(() => {});
        }
        return;
      }

      const youtube = mediaInner.querySelector(".js-youtube");
      if (youtube) {
        youtube.contentWindow?.postMessage(
          '{"event":"command","func":"playVideo","args":""}',
          "*"
        );
        return;
      }

      const vimeo = mediaInner.querySelector(".js-vimeo");
      if (vimeo) {
        vimeo.contentWindow?.postMessage('{"method":"play"}', "*");
      }
    }

    pauseAllExcept(activeSlide) {
      if (!this.swiper) return;

      this.swiper.slides.forEach(slide => {
        if (slide !== activeSlide) {
          this.pauseSlideMedia(slide);
        }
      });
    }

    playActiveSlide(swiper) {
      const activeSlide = swiper.slides[swiper.activeIndex];
      if (!activeSlide) return;

      this.pauseAllExcept(activeSlide);
      this.playSlideMedia(activeSlide);
    }
  }

  customElements.define("stl-carousel-slider", StlCarouselSlider);
}
